import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend, ResponsiveContainer } from "recharts";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const styles = (theme) => ({
  container: {
    width: "100%",
    padding: "1.5rem",
  },
  title: {
    fontSize: "1.3rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "1.5rem",
    textAlign: "center",
  },
  chartWrapper: {
    width: "100%",
    height: "500px",
    [theme.breakpoints.down("md")]: {
      height: "400px",
    },
    [theme.breakpoints.down("sm")]: {
      height: "350px",
    },
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.9)",
  },
  legendDot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
  },
});

// Dimension labels mapping
const dimensionLabels = {
  sportsmanship: "Sportsmanship",
  integrity: "Integrity",
  communication: "Communication",
  teamPlay: "Team Play",
  reliability: "Reliability",
  reporterQuality: "Reporter Quality",
};

function RadarAngleTick(props) {
  const {
    x,
    y,
    cx,
    cy,
    radius,
    angle,
    payload,
    fontFamily,
    fontSize,
    offset,
  } = props;

  const value = payload?.value ?? payload;

  // If Recharts provides polar context, compute a point further out along the axis angle.
  if (
    typeof cx === "number" &&
    typeof cy === "number" &&
    typeof radius === "number" &&
    typeof angle === "number"
  ) {
    const rad = (Math.PI / 180) * angle;
    const r = radius + offset;
    const tx = cx + r * Math.cos(-rad);
    const ty = cy + r * Math.sin(-rad);

    return (
      <text
        x={tx}
        y={ty}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="rgba(255,255,255,0.95)"
        fontFamily={fontFamily}
        fontSize={fontSize}
      >
        {value}
      </text>
    );
  }

  // Fallback (should be rare): render at provided coordinates.
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="middle"
      fill="rgba(255,255,255,0.95)"
      fontFamily={fontFamily}
      fontSize={fontSize}
    >
      {value}
    </text>
  );
}

function DimensionRadarChart({ classes, dimensions, baselines }) {
  const chartFontFamily = '"Roboto","Helvetica","Arial",sans-serif';
  const angleLabelFontSize = 15;
  const angleLabelOffset = 18;

  // Transform dimensions object into array for recharts
  const chartData = Object.keys(dimensions).map((key) => ({
    dimension: dimensionLabels[key] || key,
    yours: dimensions[key]?.score || 0,
    average: baselines?.[key] || 50,
  }));

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>
        Your Reputation vs Ouro Average
      </Typography>
      <Box className={classes.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <RadarChart data={chartData}>
            <PolarGrid stroke="rgba(255,255,255,0.2)" />
            <PolarAngleAxis
              dataKey="dimension"
              stroke="rgba(255,255,255,0.9)"
              orientation="outer"
              tickSize={12}
              tick={(tickProps) => (
                <RadarAngleTick
                  {...tickProps}
                  fontFamily={chartFontFamily}
                  fontSize={angleLabelFontSize}
                  offset={angleLabelOffset}
                />
              )}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              stroke="rgba(255,255,255,0.5)"
              tick={{
                fill: "rgba(255,255,255,0.7)",
                fontSize: 11,
                fontFamily: chartFontFamily,
              }}
            />
            <Radar
              name="Your Score"
              dataKey="yours"
              stroke="#E8A33D"
              fill="#E8A33D"
              fillOpacity={0.6}
              strokeWidth={2}
            />
            <Radar
              name="Ouro Average"
              dataKey="average"
              stroke="rgba(255,255,255,0.5)"
              fill="rgba(255,255,255,0.3)"
              fillOpacity={0.3}
              strokeWidth={2}
              strokeDasharray="5 5"
            />
            <Legend
              wrapperStyle={{
                paddingTop: "20px",
                color: "rgba(255,255,255,0.9)",
                fontFamily: chartFontFamily,
                fontSize: 14,
              }}
              iconType="circle"
            />
          </RadarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

DimensionRadarChart.propTypes = {
  classes: PropTypes.object.isRequired,
  dimensions: PropTypes.object.isRequired,
  baselines: PropTypes.object,
};

DimensionRadarChart.defaultProps = {
  baselines: {},
};

export default withStyles(styles)(DimensionRadarChart);
